/**
 * 웹 전용 idle timeout — 일정 시간 사용자 활동 없으면 자동 로그아웃.
 *
 * 데스크톱 앱은 OS keychain + 자체 refresh 흐름으로 *사실상 영구 세션* 을
 * 유지하지만, 웹은 공용 PC / 카페 등에서 사용될 가능성이 있어 보안상 idle
 * timeout 이 필요하다.
 *
 * 활동 감지: pointermove, keydown, click, scroll. 활동이 발생하면 last 시각
 * 갱신 + 타이머 reset. 마지막 활동에서 IDLE_MS 경과 시 onTimeout 호출.
 *
 * 백그라운드 탭에서 setTimeout 이 throttle 될 수 있어 "마지막 활동 시각" 을
 * Date.now() 기준으로도 검증 (가시성 변경 / focus 이벤트에서).
 */
import { useEffect } from "react";

const DEFAULT_IDLE_MS = 60 * 60 * 1_000; // 60 분

export function useIdleTimeout(
  enabled: boolean,
  onTimeout: () => void,
  idleMs: number = DEFAULT_IDLE_MS,
): void {
  useEffect(() => {
    if (!enabled) return;
    let lastActivity = Date.now();
    let timer: ReturnType<typeof setTimeout> | null = null;

    const fireIfIdle = (): void => {
      if (Date.now() - lastActivity >= idleMs) onTimeout();
      else schedule();
    };

    const schedule = (): void => {
      if (timer) clearTimeout(timer);
      const remaining = Math.max(0, idleMs - (Date.now() - lastActivity));
      timer = setTimeout(fireIfIdle, remaining + 200);
    };

    const onActivity = (): void => {
      lastActivity = Date.now();
      schedule();
    };

    const onVisible = (): void => {
      // 탭이 다시 보이면 즉시 idle 검사 — background 에서 timer throttle 된 케이스 보강.
      if (Date.now() - lastActivity >= idleMs) onTimeout();
      else schedule();
    };

    const events: (keyof WindowEventMap)[] = [
      "pointermove",
      "keydown",
      "click",
      "scroll",
    ];
    for (const ev of events) window.addEventListener(ev, onActivity, { passive: true });
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);

    schedule();
    return () => {
      if (timer) clearTimeout(timer);
      for (const ev of events) window.removeEventListener(ev, onActivity);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [enabled, onTimeout, idleMs]);
}
